import { AsyncPipe, DatePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  ApiError,
  IBreadcrumbItem,
  IOrder,
  IOrderClinicalContext,
  isPharmacyFeatures,
  OrderStatus,
  PAGE_TITLE_PARAM,
  REDIRECT_URL_PARAM,
  StorefrontUrlService,
  IOutboxRecord,
  OfflineModeService
} from '@pharvio/core/shared';
import {
  AlertService,
  AmountToMajorPipe,
  LayoutToolbarComponent,
  LoadingSpinnerComponent,
  PricePipe,
  QrCodeCardComponent,
  RemoveCharacterPipe,
  StatusBadgeColor,
  StatusBadgeComponent,
  TrackService,
  UrlClipboard
} from '@pharvio/core/ui';
import {
  CancelOrder,
  Checkout,
  CompletePacking,
  GeneratePayByLink,
  LoadOrderById,
  OrdersState,
  Pickup,
  RejectOrder,
  StartPacking,
  VerifyOrder
} from '@pharvio/orders/data-access';
import { Observable, combineLatest, map } from 'rxjs';
import {
  CancelLocalOrder,
  isRetryableOutboxError,
  LoadLocalOrders,
  mapOutboxRecordToOrder,
  OfflineDialogService,
  offlineQrStatus,
  OfflineState
} from '@pharvio/offline';
import { Clipboard } from '@angular/cdk/clipboard';
import { FeaturesState, LoadFeatures } from '@pharvio/settings/data-access';
import { OrderPaths } from '../../../../core/constants/routes/orders-routes';
import { ReturnPaths } from '../../../../core/constants/routes/returns-routes';
import { applyServerErrors } from '@pharvio/core/shared';

@Component({
  selector: 'app-order-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    DatePipe,
    UpperCasePipe,
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule,
    StatusBadgeComponent,
    LayoutToolbarComponent,
    PricePipe,
    AmountToMajorPipe,
    QrCodeCardComponent,
    UrlClipboard,
    RemoveCharacterPipe,
    LoadingSpinnerComponent
  ],
  templateUrl: './order-detail.component.html'
})
export class OrderDetailComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly track = inject(TrackService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly alert = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly storefrontUrlService = inject(StorefrontUrlService);

  private readonly offlineMode = inject(OfflineModeService);
  private readonly offlineDialog = inject(OfflineDialogService);
  private readonly clipboard = inject(Clipboard);

  private readonly orderId = this.route.snapshot.paramMap.get('id') ?? '';

  /** Offline: every server-side action is unavailable. */
  readonly isOffline = this.offlineMode.isOffline;

  private readonly isPharmacyTenant = this.store.selectSignal(
    FeaturesState.isPharmacy
  );
  private readonly branchFeatureMaps = this.store.selectSignal(
    FeaturesState.branchMaps
  );

  /** The outbox row when this id is a local (unsynced) order. */
  readonly localRecord$: Observable<IOutboxRecord | null> = this.store
    .select(OfflineState.localOrders)
    .pipe(
      map(
        (item) =>
          item.data.find((r) => r.clientOrderId === this.orderId) ?? null
      )
    );

  readonly order$: Observable<IOrder | null> = combineLatest([
    this.store.select(OrdersState.selectSelectedOrder),
    this.localRecord$
  ]).pipe(
    map(([server, local]) => (local ? mapOutboxRecordToOrder(local) : server))
  );

  readonly orderStillValid$: Observable<boolean> = this.order$.pipe(
    map((order) => {
      if (!order) return false;
      if (!order.paymentLinkExpiresAt) return true;
      const expiresAt = new Date(order.paymentLinkExpiresAt);
      return expiresAt > new Date();
    })
  );

  readonly orderPaymentLink$: Observable<string | null> = this.order$.pipe(
    map((order) => {
      if (!order) return null;
      if (
        !order.paymentLinkExpiresAt ||
        !order.paymentLinkToken ||
        order.status === 'completed'
      )
        return null;
      const expiresAt = new Date(order.paymentLinkExpiresAt);
      const isValid = expiresAt > new Date();
      // const isValid = true;
      if (!isValid) return null;

      return this.storefrontUrlService.createCheckoutByTokenUrl(
        order.paymentLinkToken
      );
    })
  );

  readonly orderPaths = OrderPaths;
  readonly returnPaths = ReturnPaths;
  readonly lineColumns = ['item', 'qty', 'unitPrice', 'tax', 'subtotal'];

  readonly crumbs$: Observable<IBreadcrumbItem[]> = this.route.queryParams.pipe(
    map((params) => {
      const crumbs: IBreadcrumbItem[] = [];
      if (params[PAGE_TITLE_PARAM] && params[REDIRECT_URL_PARAM]) {
        crumbs.push({
          text: params[PAGE_TITLE_PARAM],
          link: params[REDIRECT_URL_PARAM]
        });
      } else {
        crumbs.push({
          text: 'Orders',
          link: this.orderPaths.List
        });
      }
      crumbs.push('Order Detail');
      return crumbs;
    })
  );

  showCancelForm = false;
  showRejectForm = false;
  busy = false;
  loading = signal(false);

  readonly cancelForm = this.fb.group({
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });
  readonly rejectForm = this.fb.group({
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  ngOnInit(): void {
    // Keep the list warm (badges, back-nav) and refresh this specific order.
    if (!this.orderId) {
      this.alert.error('Order ID is missing in the route.');
      this.router.navigate([this.orderPaths.List]);
      return;
    }
    this.loading.set(true);
    this.store.dispatch(new LoadLocalOrders());
    this.store
      .dispatch(new LoadOrderById(this.orderId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => {
          this.loading.set(false);
          this.cdr.markForCheck();
          this.loadBranchFeatures();
        }
      });
  }

  /**
   * The exact answer for the branch that took this order. A storefront order has
   * no branch, so nothing is fetched and the tenant-wide map answers instead.
   */
  private loadBranchFeatures(): void {
    const branchId = this.orderBranchId(
      this.store.selectSnapshot(OrdersState.selectSelectedOrder)
    );
    if (branchId) this.store.dispatch(new LoadFeatures(branchId));
  }

  private orderBranchId(order: IOrder | null): string | null {
    return order?.createdInBranchId ?? order?.branchId ?? null;
  }

  /**
   * Whether to show the health answers collected against this order.
   *
   * Pharmacy-only, and "pharmacy" can come from either side of the order: the
   * branch that took it dispenses medicine, or — for a storefront order, which
   * has no branch of its own — the storefront does, which is the tenant-wide
   * answer the BE derives `storeKind` from. Either one is enough.
   *
   * Reads the maps signal directly so the card appears once a branch map lands,
   * and fails towards pharmacy like every other read of the feature map.
   */
  showClinicalInfo(order: IOrder): boolean {
    if (!order.clinicalContext) return false;
    const branchId = this.orderBranchId(order);
    const map = branchId ? this.branchFeatureMaps()[branchId] : undefined;
    if (!map) return this.isPharmacyTenant();
    return isPharmacyFeatures(map.features) || this.isPharmacyTenant();
  }

  /** The four yes/no answers, in the order the storefront asks them. */
  clinicalAnswers(
    clinical: IOrderClinicalContext
  ): { label: string; value: boolean }[] {
    return [
      { label: 'Pregnant', value: clinical.isPregnant },
      { label: 'Breastfeeding', value: clinical.isBreastfeeding },
      { label: 'Under 18', value: clinical.isMinor },
      { label: 'On other medications', value: clinical.isOnMedications }
    ];
  }

  // ── Status display ─────────────────────────────────────────────────────────

  statusBadge(status: OrderStatus): { text: string; color: StatusBadgeColor } {
    const map: Record<OrderStatus, { text: string; color: StatusBadgeColor }> =
      {
        draft: { text: 'Draft', color: 'neutral' },
        pending_payment: { text: 'Pending Payment', color: 'warning' },
        payment_received: { text: 'Payment Received', color: 'info' },
        awaiting_verification: {
          text: 'Awaiting Verification',
          color: 'warning'
        },
        packing: { text: 'Packing', color: 'info' },
        confirmed: { text: 'Confirmed', color: 'info' },
        packed: { text: 'Packed', color: 'success' },
        completed: { text: 'Completed', color: 'success' },
        verified: { text: 'Verified', color: 'success' },
        cancelled: { text: 'Cancelled', color: 'neutral' },
        rejected: { text: 'Rejected', color: 'neutral' }
      };
    return map[status];
  }

  paymentLink(order: IOrder): string | null {
    return order.expiredAt && new Date(order.expiredAt) > new Date()
      ? (order.paymentLinkUrl ?? order.paymentLinkToken ?? null)
      : null;
  }

  canRegenerate(order: IOrder): string | null {
    return order.paymentLinkUrl ?? order.paymentLinkToken ?? null;
  }

  formatAddress(order: IOrder): string | null {
    const a = order.checkoutAddress as Record<string, string> | undefined;
    if (!a) return null;
    const parts = [
      a['streetAddress'] ?? a['street'],
      a['city'],
      a['region'] ?? a['state']
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }

  // ── Action visibility ────────────────────────────────────────────────────

  /** Pay-by-link / checkout can only initialise before money has changed hands — and only online. */
  canInitiatePayment(order: IOrder): boolean {
    return (
      !this.isOffline() &&
      (order.status === 'draft' || order.status === 'pending_payment') &&
      order.source !== 'customer_self_serve'
    );
  }

  canCancel(order: IOrder): boolean {
    return (
      !this.isOffline() &&
      !['cancelled', 'completed', 'rejected'].includes(order.status)
    );
  }

  // ── Local (unsynced) orders ────────────────────────────────────────────

  /**
   * The only action on a local order: cancel it — before it is sent, after
   * the server rejected it, or while a QR is still unpaid. Cancelling a cash
   * sale releases its local stock decrement.
   */
  canCancelLocal(record: IOutboxRecord): boolean {
    return (
      record.status === 'pending' ||
      record.status === 'failed' ||
      record.status === 'issued'
    );
  }

  /** A QR order the customer can still scan and pay. */
  qrPayable(record: IOutboxRecord): boolean {
    return offlineQrStatus(record) === 'payable';
  }

  showQr(record: IOutboxRecord): void {
    if (!this.offlineDialog.openQrForRecord(record)) {
      this.alert.warning('This QR code has expired. Create the order again.');
    }
  }

  copyQrLink(record: IOutboxRecord): void {
    if (!record.qr || !this.qrPayable(record)) return;
    this.clipboard.copy(record.qr.url);
    this.alert.success('Payment link copied.');
  }

  localStatusLabel(record: IOutboxRecord): string {
    if (record.status === 'issued' && offlineQrStatus(record) === 'expired') {
      return 'QR expired — the customer can no longer pay with it. Cancel and create the order again.';
    }
    switch (record.status) {
      case 'pending':
        return 'Recorded offline — waiting to sync';
      case 'syncing':
        return 'Syncing…';
      case 'failed':
        return isRetryableOutboxError(record.errorCode)
          ? `Sync failed${record.lastError ? `: ${record.lastError}` : ''} — will retry`
          : `Rejected by the server${record.lastError ? `: ${record.lastError}` : ''} — cancel to release stock`;
      case 'issued':
        return 'QR issued — the customer completes payment on their phone';
      case 'redeemed':
        return 'Paid by the customer via QR';
      case 'cancelled':
        return 'Cancelled on this device (never sent)';
      default:
        return record.status;
    }
  }

  cancelLocal(record: IOutboxRecord): void {
    this.alert
      .showConfirmationModal({
        title: 'Cancel offline order',
        message: `Cancel ${record.clientReference}? It has not been sent to the server and will be discarded.`,
        primaryButton: { title: 'Cancel Order', value: true },
        secondaryButton: { title: 'Keep', value: false }
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.run(new CancelLocalOrder(record.clientOrderId), {
          success: 'Offline order cancelled.'
        });
      });
  }

  // ── Payment actions ──────────────────────────────────────────────────────

  generatePayByLink(order: IOrder): void {
    this.run(
      new GeneratePayByLink(order.id, { ttlMinutes: 30 }),
      { success: 'Pay-by-link generated.' },
      () =>
        this.track.track({
          name: 'order_pay_link_sent',
          props: { orderId: order.id }
        })
    );
  }

  checkout(order: IOrder): void {
    this.run(new Checkout(order.id, {}), {
      success: 'Checkout initialised — payment link ready.'
    });
  }

  copyPaymentLink(order: IOrder): void {
    const link = this.paymentLink(order);
    if (!link) return;
    navigator.clipboard
      ?.writeText(link)
      .then(() => this.alert.success('Payment link copied.'))
      .catch(() => this.alert.error('Could not copy the link.'));
  }

  // ── Fulfillment actions ──────────────────────────────────────────────────

  verify(order: IOrder): void {
    this.run(
      new VerifyOrder(order.id, {}),
      { success: 'Order verified.' },
      () =>
        this.track.track({
          name: 'order_payment_verified',
          props: { orderId: order.id }
        })
    );
  }

  startPacking(order: IOrder): void {
    this.run(new StartPacking(order.id), { success: 'Packing started.' }, () =>
      this.trackTransition(order, 'packing')
    );
  }

  completePacking(order: IOrder): void {
    this.run(
      new CompletePacking(order.id, {}),
      { success: 'Packing completed.' },
      () => this.trackTransition(order, 'packed')
    );
  }

  pickup(order: IOrder): void {
    this.run(new Pickup(order.id), { success: 'Marked as picked up.' }, () =>
      this.trackTransition(order, 'picked_up')
    );
  }

  // ── Cancel (inline form) ─────────────────────────────────────────────────

  showCancelPrompt(): void {
    this.cancelForm.reset();
    this.showRejectForm = false;
    this.showCancelForm = true;
  }

  dismissCancel(): void {
    this.showCancelForm = false;
    this.cancelForm.reset();
  }

  submitCancel(order: IOrder): void {
    if (this.cancelForm.invalid) {
      this.cancelForm.markAllAsTouched();
      return;
    }
    this.store
      .dispatch(
        new CancelOrder(order.id, {
          reason: this.cancelForm.getRawValue().reason
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.trackTransition(order, 'cancelled');
          this.alert.success('Order cancelled.');
          this.router.navigate([this.orderPaths.List]);
        },
        error: (err) => {
          applyServerErrors(this.cancelForm, err);
          this.cdr.markForCheck();
        }
      });
  }

  // ── Reject (inline form, Rx verification) ────────────────────────────────

  showRejectPrompt(): void {
    this.rejectForm.reset();
    this.showCancelForm = false;
    this.showRejectForm = true;
  }

  dismissReject(): void {
    this.showRejectForm = false;
    this.rejectForm.reset();
  }

  submitReject(order: IOrder): void {
    if (this.rejectForm.invalid) {
      this.rejectForm.markAllAsTouched();
      return;
    }
    this.run(
      new RejectOrder(order.id, {
        reason: this.rejectForm.getRawValue().reason
      }),
      { success: 'Order rejected.' },
      () => this.trackTransition(order, 'rejected')
    );
    this.dismissReject();
  }

  // ── Shared dispatch helper ───────────────────────────────────────────────

  /** `order` is captured before the dispatch, so `fromStatus` is the status
   *  the transition actually started from rather than the refreshed one. */
  private trackTransition(order: IOrder, toStatus: string): void {
    this.track.track({
      name: 'order_status_changed',
      props: { orderId: order.id, fromStatus: order.status, toStatus }
    });
  }

  private run(
    action: object,
    msg: { success: string },
    onSuccess?: () => void
  ): void {
    if (this.busy) return;
    this.busy = true;
    this.store
      .dispatch(action)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy = false;
          onSuccess?.();
          this.alert.success(msg.success);
          this.cdr.markForCheck();
        },
        error: (err: ApiError) => {
          this.busy = false;
          this.alert.error(err?.message ?? 'Something went wrong.');
          this.cdr.markForCheck();
        }
      });
  }
}
